import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './RasterMap.module.css';

const TILE_SIZE = 256;
const MIN_ZOOM = 4;
const MAX_ZOOM = 18;

export interface RasterMapPoint {
  readonly id: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly label: string;
  readonly selected?: boolean;
  readonly kind?: 'listing' | 'building';
}

export interface RasterViewport {
  readonly west: number;
  readonly south: number;
  readonly east: number;
  readonly north: number;
  readonly zoom: number;
}

interface RasterMapProps {
  readonly points: readonly RasterMapPoint[];
  readonly onSelect?: (id: string) => void;
  readonly onViewportChange?: (viewport: RasterViewport) => void;
  readonly ariaLabel?: string;
  readonly initialLatitude?: number;
  readonly initialLongitude?: number;
  readonly initialZoom?: number;
  readonly drawing?: boolean;
  readonly zone?: readonly GeoPoint[];
  readonly onDrawComplete?: (zone: readonly GeoPoint[]) => void;
}

interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

export interface GeoPoint {
  readonly latitude: number;
  readonly longitude: number;
}

interface DrawState {
  readonly pointerId: number;
  readonly points: readonly GeoPoint[];
  readonly lastX: number;
  readonly lastY: number;
}

interface DragState {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly centerWorld: WorldPoint;
}

interface HostSize {
  readonly width: number;
  readonly height: number;
}

function clampLatitude(latitude: number): number {
  return Math.max(-85.05112878, Math.min(85.05112878, latitude));
}

function worldPoint(
  latitude: number,
  longitude: number,
  zoom: number,
): WorldPoint {
  const scale = TILE_SIZE * 2 ** zoom;
  const lat = (clampLatitude(latitude) * Math.PI) / 180;
  const sin = Math.sin(lat);
  return {
    x: ((longitude + 180) / 360) * scale,
    y:
      (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) *
      scale,
  };
}

function geoPoint(x: number, y: number, zoom: number): GeoPoint {
  const scale = TILE_SIZE * 2 ** zoom;
  const longitude = (x / scale) * 360 - 180;
  const normalizedY = Math.max(0, Math.min(scale, y)) / scale;
  const latitude =
    (Math.atan(Math.sinh(Math.PI * (1 - 2 * normalizedY))) * 180) /
    Math.PI;
  return {
    latitude: clampLatitude(latitude),
    longitude: ((longitude + 540) % 360) - 180,
  };
}

function viewportFor(
  host: HTMLDivElement | null,
  latitude: number,
  longitude: number,
  zoom: number,
): RasterViewport {
  const width =
    host?.clientWidth !== undefined && host.clientWidth > 0
      ? host.clientWidth
      : 1024;
  const height =
    host?.clientHeight !== undefined && host.clientHeight > 0
      ? host.clientHeight
      : 560;
  const center = worldPoint(latitude, longitude, zoom);
  const northWest = geoPoint(
    center.x - width / 2,
    center.y - height / 2,
    zoom,
  );
  const southEast = geoPoint(
    center.x + width / 2,
    center.y + height / 2,
    zoom,
  );
  return {
    west: northWest.longitude,
    south: southEast.latitude,
    east: southEast.longitude,
    north: northWest.latitude,
    zoom,
  };
}

function tileUrl(x: number, y: number, zoom: number): string {
  const count = 2 ** zoom;
  const wrappedX = ((x % count) + count) % count;
  const clampedY = Math.max(0, Math.min(count - 1, y));
  return `https://tile.openstreetmap.org/${String(zoom)}/${String(
    wrappedX,
  )}/${String(clampedY)}.png`;
}

export function RasterMap({
  points,
  onSelect,
  onViewportChange,
  ariaLabel = 'Mapa de propiedades',
  initialLatitude = 19.4326,
  initialLongitude = -99.1332,
  initialZoom = 13,
  drawing = false,
  zone = [],
  onDrawComplete,
}: RasterMapProps): React.JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const drawRef = useRef<DrawState | null>(null);
  const [hostSize, setHostSize] = useState<HostSize>({
    width: 1024,
    height: 560,
  });
  const [draftZone, setDraftZone] = useState<readonly GeoPoint[]>([]);
  const [latitude, setLatitude] = useState(initialLatitude);
  const [longitude, setLongitude] = useState(initialLongitude);
  const [zoom, setZoom] = useState(
    Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, initialZoom)),
  );

  const center = worldPoint(latitude, longitude, zoom);
  const centerTileX = Math.floor(center.x / TILE_SIZE);
  const centerTileY = Math.floor(center.y / TILE_SIZE);

  const tiles = useMemo(() => {
    const result: { readonly x: number; readonly y: number }[] = [];
    for (let y = -3; y <= 3; y += 1) {
      for (let x = -4; x <= 4; x += 1) {
        result.push({ x: centerTileX + x, y: centerTileY + y });
      }
    }
    return result;
  }, [centerTileX, centerTileY]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const host = hostRef.current;
    if (host === null) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry === undefined) return;
      const width = Math.round(entry.contentRect.width);
      const height = Math.round(entry.contentRect.height);
      if (width < 1 || height < 1) return;
      setHostSize(current =>
        current.width === width && current.height === height
          ? current
          : { width, height },
      );
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (onViewportChange === undefined) return;
    const timer = window.setTimeout(() => {
      onViewportChange(
        viewportFor(hostRef.current, latitude, longitude, zoom),
      );
    }, 220);
    return () => window.clearTimeout(timer);
  }, [hostSize, latitude, longitude, onViewportChange, zoom]);

  function changeZoom(delta: number): void {
    setZoom(current =>
      Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, current + delta)),
    );
  }

  function recenter(): void {
    setLatitude(initialLatitude);
    setLongitude(initialLongitude);
    setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, initialZoom)));
  }

  function geoFromEvent(
    event: React.PointerEvent<HTMLDivElement>,
  ): GeoPoint {
    const host = hostRef.current;
    const rect = host?.getBoundingClientRect();
    const width = rect?.width !== undefined && rect.width > 0 ? rect.width : 1024;
    const height =
      rect?.height !== undefined && rect.height > 0 ? rect.height : 560;
    const left = rect?.left ?? 0;
    const top = rect?.top ?? 0;
    const centerWorld = worldPoint(latitude, longitude, zoom);

    return geoPoint(
      centerWorld.x + event.clientX - left - width / 2,
      centerWorld.y + event.clientY - top - height / 2,
      zoom,
    );
  }

  function beginDrag(event: React.PointerEvent<HTMLDivElement>): void {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);

    if (drawing) {
      const point = geoFromEvent(event);
      const drawPoints = [point];
      drawRef.current = {
        pointerId: event.pointerId,
        points: drawPoints,
        lastX: event.clientX,
        lastY: event.clientY,
      };
      setDraftZone(drawPoints);
      return;
    }

    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      centerWorld: worldPoint(latitude, longitude, zoom),
    };
  }

  function moveDrag(event: React.PointerEvent<HTMLDivElement>): void {
    if (drawing) {
      const draw = drawRef.current;
      if (draw?.pointerId !== event.pointerId) return;
      const distance = Math.hypot(
        event.clientX - draw.lastX,
        event.clientY - draw.lastY,
      );
      if (distance < 5) return;

      const point = geoFromEvent(event);
      const drawPoints = [...draw.points, point];
      drawRef.current = {
        pointerId: draw.pointerId,
        points: drawPoints,
        lastX: event.clientX,
        lastY: event.clientY,
      };
      setDraftZone(drawPoints);
      return;
    }

    const drag = dragRef.current;
    if (drag?.pointerId !== event.pointerId) return;
    const moved = geoPoint(
      drag.centerWorld.x - (event.clientX - drag.startX),
      drag.centerWorld.y - (event.clientY - drag.startY),
      zoom,
    );
    setLatitude(moved.latitude);
    setLongitude(moved.longitude);
  }

  function endDrag(event: React.PointerEvent<HTMLDivElement>): void {
    if (drawing && drawRef.current?.pointerId === event.pointerId) {
      const drawPoints = drawRef.current.points;
      drawRef.current = null;
      setDraftZone([]);
      if (drawPoints.length >= 3) onDrawComplete?.(drawPoints);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      return;
    }

    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  const visibleZone = draftZone.length >= 2 ? draftZone : zone;
  const zonePoints = visibleZone
    .map(point => {
      const projected = worldPoint(point.latitude, point.longitude, zoom);
      const x = projected.x - center.x + hostSize.width / 2;
      const y = projected.y - center.y + hostSize.height / 2;
      return `${String(x)},${String(y)}`;
    })
    .join(' ');

  return (
    <div
      ref={hostRef}
      className={styles.map}
      role="region"
      aria-label={ariaLabel}
      data-drawing={drawing ? 'true' : 'false'}
      onPointerDown={beginDrag}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div className={styles.tiles} aria-hidden="true">
        {tiles.map(tile => {
          const tileLeft = tile.x * TILE_SIZE - center.x;
          const tileTop = tile.y * TILE_SIZE - center.y;
          return (
            <img
              key={`${String(zoom)}-${String(tile.x)}-${String(tile.y)}`}
              className={styles.tile}
              src={tileUrl(tile.x, tile.y, zoom)}
              alt=""
              draggable={false}
              style={{
                left: `calc(50% + ${String(tileLeft)}px)`,
                top: `calc(50% + ${String(tileTop)}px)`,
              }}
            />
          );
        })}
      </div>

      {zonePoints !== '' ? (
        <svg
          className={styles.zoneOverlay}
          viewBox={`0 0 ${String(hostSize.width)} ${String(hostSize.height)}`}
          aria-hidden="true"
        >
          <polygon points={zonePoints} />
        </svg>
      ) : null}

      <div className={styles.markers}>
        {points.map(point => {
          const marker = worldPoint(point.latitude, point.longitude, zoom);
          const left = marker.x - center.x;
          const top = marker.y - center.y;
          return (
            <button
              key={point.id}
              type="button"
              className={
                point.selected
                  ? styles.markerActive
                  : point.kind === 'building'
                    ? styles.markerBuilding
                    : styles.marker
              }
              style={{
                left: `calc(50% + ${String(left)}px)`,
                top: `calc(50% + ${String(top)}px)`,
              }}
              onPointerDown={event => event.stopPropagation()}
              onClick={() => onSelect?.(point.id)}
              aria-label={point.label}
            >
              {point.label}
            </button>
          );
        })}
      </div>

      <div
        className={styles.controls}
        onPointerDown={event => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => changeZoom(1)}
          disabled={zoom >= MAX_ZOOM}
          aria-label="Acercar mapa"
        >
          ＋
        </button>
        <button
          type="button"
          onClick={() => changeZoom(-1)}
          disabled={zoom <= MIN_ZOOM}
          aria-label="Alejar mapa"
        >
          −
        </button>
        <button type="button" onClick={recenter} aria-label="Centrar mapa">
          ◎
        </button>
      </div>

      <div className={styles.zoomBadge}>Zoom {zoom}</div>
      <a
        className={styles.attribution}
        href="https://www.openstreetmap.org/copyright"
        target="_blank"
        rel="noreferrer"
        onPointerDown={event => event.stopPropagation()}
      >
        © OpenStreetMap contributors
      </a>
    </div>
  );
}
